import { VisionMonitorOperator } from '@/services/VisionMonitorService';

export type FilterFieldType = 'text' | 'number' | 'boolean' | 'enum';

export interface FilterField {
  field: string;
  label: string;
  type: FilterFieldType;
  options?: string[];
}

// Keep in sync with VISION_MONITOR_FIELDS in catalog
// (src/products/vision-monitor/vision-monitor-filters.ts).
export const FILTER_FIELDS: FilterField[] = [
  { field: 'catalogId', label: 'Catalog ID', type: 'number' },
  { field: 'productId', label: 'Product ID', type: 'text' },
  { field: 'sku', label: 'SKU', type: 'text' },
  { field: 'productName', label: 'Product name', type: 'text' },
  { field: 'customerName', label: 'Customer name', type: 'text' },
  { field: 'visionCategory', label: 'Vision category', type: 'text' },
  {
    field: 'visionCategoryStatus',
    label: 'Vision category status',
    type: 'enum',
    options: ['PENDING', 'SUCCESS', 'ERROR'],
  },
  {
    field: 'visionCategoryError',
    label: 'Vision category error',
    type: 'text',
  },
  {
    field: 'visionCategoryAttempts',
    label: 'Vision category attempts',
    type: 'number',
  },
  { field: 'needVisionRun', label: 'Need vision run', type: 'boolean' },
  { field: 'sentToVision', label: 'Sent to vision', type: 'boolean' },
  { field: 'visionRun', label: 'Vision run', type: 'boolean' },
  {
    field: 'visionStatus',
    label: 'Vision status',
    type: 'enum',
    options: ['pending', 'success', 'error', 'synced'],
  },
  { field: 'visionError', label: 'Vision error', type: 'text' },
  { field: 'visionAttempts', label: 'Vision attempts', type: 'number' },
  { field: 'visionReadCount', label: 'Vision read count', type: 'number' },
  { field: 'stock', label: 'Stock', type: 'boolean' },
  { field: 'isActive', label: 'Active', type: 'boolean' },
  {
    field: 'seoStatus',
    label: 'SEO status',
    type: 'enum',
    options: ['PENDING', 'SUCCESS', 'ERROR'],
  },
  { field: 'seoSynced', label: 'SEO synced', type: 'boolean' },
  { field: 'seoError', label: 'SEO error', type: 'text' },
];

export const OPERATORS: Record<
  FilterFieldType,
  { value: VisionMonitorOperator; label: string; needsValue: boolean }[]
> = {
  text: [
    { value: 'contains', label: 'contains', needsValue: true },
    { value: 'equals', label: 'equals', needsValue: true },
    { value: 'notEquals', label: 'does not equal', needsValue: true },
    { value: 'isEmpty', label: 'is empty', needsValue: false },
    { value: 'isNotEmpty', label: 'is not empty', needsValue: false },
  ],
  number: [
    { value: 'eq', label: '=', needsValue: true },
    { value: 'gte', label: '≥', needsValue: true },
    { value: 'lte', label: '≤', needsValue: true },
  ],
  boolean: [{ value: 'is', label: 'is', needsValue: true }],
  enum: [
    { value: 'in', label: 'is any of', needsValue: true },
    { value: 'notIn', label: 'is none of', needsValue: true },
  ],
};

export const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
  { value: 'null', label: 'Not set' },
];

export function getFilterField(field: string) {
  return FILTER_FIELDS.find((item) => item.field === field);
}

export function getDefaultOperator(field: string): VisionMonitorOperator {
  const type = getFilterField(field)?.type ?? 'text';

  return OPERATORS[type][0].value;
}
